// Cesty sekcí — jediná mapa klíč sekce (docs/08-autorizace.md §2) ↔ cesta v aplikaci:
// `muj_den` → `/muj-den`, `sklad_evidence:evidence` → `/sklad-evidence/evidence`, `home` → `/`.
// Ke každé sekci i fáze a krok z docs/06-plan-implementace.md, ve které dostane skutečnou obrazovku
// (do té doby ji zastupuje sekce/Zastupna.tsx).

export interface FazeSekce {
  /** Fáze plánu, např. `F4`. */
  faze: string;
  /** Krok plánu, např. `4.4`. */
  krok: string;
}

/** Fáze a krok z docs/06, ve kterém sekce vzniká (klíče podle 08 §2). */
export const FAZE_SEKCI: Readonly<Record<string, FazeSekce>> = {
  home: { faze: 'F1', krok: '1.3' },
  kalendar: { faze: 'F4', krok: '4.3' },
  zakazky: { faze: 'F4', krok: '4.1' },
  // Audity (rozhodnutí zadavatele 24. 10. 2026, W80): samostatný modul auditů u zákazníků.
  audity: { faze: 'F11', krok: 'W80' },
  muj_den: { faze: 'F4', krok: '4.4' },
  mapa_provozu: { faze: 'F11', krok: '11.11' },
  // Auta na cestě (zadání zadavatele 31. 10. 2026, W95): skladník „Kdo k nám jede“, řidič „Kde jsou naše auta“.
  auta_na_ceste: { faze: 'F11', krok: 'W95' },
  svoz_pozadavky: { faze: 'F4', krok: '4.8' },
  sklad_evidence: { faze: 'F8', krok: '8.1' },
  'sklad_evidence:evidence': { faze: 'F9', krok: '9.1' },
  'sklad_evidence:ilno': { faze: 'F9', krok: '9.5' },
  vazeni: { faze: 'F5', krok: '5.1' },
  celni_sprava: { faze: 'F9', krok: '9.7' },
  zakaznici: { faze: 'F3', krok: '3.1' },
  // Odběratelé (rozhodnutí zadavatele 8. 10. 2026, W40): firmy, kterým prodáváme materiál.
  odberatele: { faze: 'F3', krok: 'W40' },
  kontakty: { faze: 'F3', krok: '3.2' },
  smlouvy: { faze: 'F7', krok: '7.3' },
  ecosmlouvy: { faze: 'F7', krok: '7.6' },
  ucetnictvi: { faze: 'F6', krok: '6.6' },
  dochazka: { faze: 'F10', krok: '10.2' },
  ke_kontrole: { faze: 'F4', krok: '4.7' },
  pohledavky: { faze: 'F6', krok: '6.7' },
  export_ucetni: { faze: 'F12', krok: '12.4' },
  pokladny: { faze: 'F6', krok: '6.21' },
  poptavky: { faze: 'F7', krok: '7.1' },
  nabidky: { faze: 'F7', krok: '7.2' },
  moje_firmy: { faze: 'F3', krok: '3.7' },
  pipeline: { faze: 'F7', krok: '7.2' },
  interakce: { faze: 'F3', krok: '3.7' },
  iso: { faze: 'F11', krok: '11.1' },
  vozidla: { faze: 'F3', krok: '3.6' },
  ceniky: { faze: 'F3', krok: '3.4' },
  sklad: { faze: 'F3', krok: '3.3' },
  statistiky: { faze: 'F9', krok: '9.9' },
  interni_cenik: { faze: 'F3', krok: '3.4' },
  vzory_dokumentu: { faze: 'F6', krok: '6.10' },
  archiv_dokumentu: { faze: 'F6', krok: '6.2' },
  zamestnanci: { faze: 'F2', krok: '2.7' },
  // Zájemci o práci (pokyn zadavatele 7. 10. 2026) — mimo plán, zapsáno ke kroku karty zaměstnance.
  'zamestnanci:zajemci': { faze: 'F10', krok: '10.1' },
  nastaveni: { faze: 'F2', krok: '2.1' },
  hlaseni: { faze: 'F12', krok: '12.5' },
  asistent: { faze: 'F12', krok: '12.3' },
  pravidla_ai: { faze: 'F12', krok: '12.3' },
  znalostni_baze: { faze: 'F3', krok: '3.9' },
  // Jedna fronta K vyřízení (05/18) — plán 06 „ke kroku 12.5“, postaveno W24.
  k_vyrizeni: { faze: 'F12', krok: '12.5' },
};

/** Sekce, které už mají skutečnou obrazovku (F1 1.4/1.5: Zaměstnanci, Nastavení) — ostatní zastupuje Zastupna. */
export const SEKCE_S_OBRAZOVKOU: ReadonlySet<string> = new Set([
  'home',
  'zamestnanci',
  'nastaveni',
  'zakaznici',
  'kontakty',
  // Zakázky (F4 4.1): seznam, nová zakázka, karta se stavovým automatem.
  'zakazky',
  // Audity (24. 10. 2026, W80): seznam, přehled podle auditorů, firmy auditorů, nový audit a detail.
  'audity',
  // AI asistent (krok 3.8): centrum asistenta a Pravidla AI.
  'asistent',
  'pravidla_ai',
  // Katalog položek (krok 3.3) a Vozidla (krok 3.6).
  'sklad',
  'vozidla',
  // Systém řízení (F11): kostra se záložkami, obrazovky přibývají po krocích 11.1–11.8.
  'iso',
  // Evidenční subjekty (krok 3.5): Evidence a přeprava › Subjekty a základ fronty Ke kontrole.
  'sklad_evidence:evidence',
  // ILNO (krok 9.5): karty odpadu, Identifikační listy nebezpečného odpadu a knihovna popisů.
  'sklad_evidence:ilno',
  'ke_kontrole',
  // Obchod (krok 3.7): Moje firmy (obchodní databáze) a Aktivity.
  'moje_firmy',
  'interakce',
  // Ceníky a Interní ceník (krok 3.4).
  'ceniky',
  'interni_cenik',
  // Znalostní báze (krok 3.9).
  'znalostni_baze',
  // Kalendář (krok 4.3): měsíc / týden / vlastní rozsah, poznámky a úkoly.
  'kalendar',
  // Můj den (krok 4.4): tablet řidiče / skladníka / koordinátora, přehled dne pro kancelář.
  'muj_den',
  // Požadavky na svoz (krok 4.8): seznam, požadavek, naplánovat / zamítnout / zrušit.
  'svoz_pozadavky',
  // Vážení (kroky 5.1 a 5.2): panel váhy, 1. a 2. vážení, archiv, vozidla a táry, nastavení.
  'vazeni',
  // Náš sklad (krok 8.1): stav skladu a žurnál pohybů s ruční korekcí.
  'sklad_evidence',
  // Poptávky (krok 7.1): seznam se záložkami, nová poptávka s pomocníkem, detail s převzetím a uzavřením.
  'poptavky',
  // Docházka (krok 10.2): moje směna s příchodem a odchodem, přestávka i zpětně, měsíční přehled.
  'dochazka',
  // Nabídky a Pipeline (krok 7.2): nacenění z ceníků s marží, odeslání s otiskem a přehled obchodů podle fáze.
  'nabidky',
  'pipeline',
  // Smlouvy (krok 7.3): generování ze vzoru, příloha č. 1 s cenami a dopravou, znění s otiskem, PDF a podpis.
  'smlouvy',
  // ECOsmlouvy (krok 7.6): přehled plnění servisních smluv, vyúčtování paušálů a inflační indexace.
  'ecosmlouvy',
  // Archiv dokumentů (krok 6.2): otisky vystavených dokumentů, filtry, náhled otisku a PDF.
  'archiv_dokumentu',
  // Pohledávky (krok 6.7): faktury po splatnosti, upomínky, splátkové kalendáře, prominutí pokuty.
  'pohledavky',
  // Účetnictví (krok 6.6): zatím jen záložka Banka — pohyby, párování na faktury a dávky ke čtení.
  'ucetnictvi',
  // Vzory dokumentů (krok 6.10): galerie tištěných dokumentů, editor bloků s živým náhledem, připomínky.
  'vzory_dokumentu',
  // Celní správa (krok 9.7): evidence ZMO, oznámení DA‑215 (§ 50) a měsíční oznámení množství.
  'celni_sprava',
  // Statistiky (krok 9.9): záložky Řidiči, Vozidla, Evidence a — jen admin a jednatel — Zákazníci, Ziskovost.
  'statistiky',
  // Hlášení a připomínky (krok 12.5): závady a náměty k aplikaci, stav a odpověď hlásícímu.
  'hlaseni',
  // Mapa provozu (krok 11.11, GPS etapa 2 část F): vozidla, kontejnery s jednotkou, zakázky a audity dne na mapě.
  'mapa_provozu',
  // Auta na cestě (W95): skladník „Kdo k nám jede“, řidič „Kde jsou naše auta“ — jen aktuální polohy v provozní době.
  'auta_na_ceste',
  // Export do účetnictví (krok 12.4): vydané doklady za období jako ZIP s ISDOC pro PROFIT.
  'export_ucetni',
  // K vyřízení (05/18, W24): Ke kontrole, Hlášení, ISŘ události a Kontroly souladu v jedné frontě.
  'k_vyrizeni',
  // Pokladny (krok 6.21): pokladna skladu a kanceláře — příjem k fakturám, převody, uzávěrky a pohyby s doklady.
  'pokladny',
  // Zájemci o práci (7. 10. 2026): uchazeči o zaměstnání, lhůta uchování 3 roky a skutečné mazání.
  'zamestnanci:zajemci',
  // Odběratelé (8. 10. 2026, W40): firmy s rolí odběratel, řádek otevře kartu na záložce Prodej.
  'odberatele',
]);

/** Má sekce skutečnou obrazovku (ne zástupnou)? */
export function maObrazovku(klic: string): boolean {
  return SEKCE_S_OBRAZOVKOU.has(klic);
}

/** Všechny známé klíče sekcí (pořadí = tovární pořadí menu z prototypu `MENU_TOVARNI_PORADI`). */
export const KLICE_SEKCI: readonly string[] = Object.keys(FAZE_SEKCI);

/** Cesta z klíče: `_` → `-`, `:` → `/`; Domů je kořen. */
function odvoditCestu(klic: string): string {
  return klic === 'home' ? '/' : `/${klic.replace(/_/g, '-').replace(/:/g, '/')}`;
}

/** Klíč sekce → cesta v aplikaci. */
export const CESTY_SEKCI: Readonly<Record<string, string>> = Object.fromEntries(
  KLICE_SEKCI.map((klic) => [klic, odvoditCestu(klic)]),
);

const KLICE_PODLE_CESTY: ReadonlyMap<string, string> = new Map(
  KLICE_SEKCI.map((klic) => [CESTY_SEKCI[klic] as string, klic]),
);

/** Cesta sekce podle klíče; neznámý klíč se odvodí stejným pravidlem. */
export function cestaSekce(klic: string): string {
  return CESTY_SEKCI[klic] ?? odvoditCestu(klic);
}

/**
 * Klíč sekce z cesty (bez query); neznámá cesta → undefined. Podcesty patří sekci nejdelší
 * shodné předpony: `/zamestnanci/{id}/ucet` → `zamestnanci`, `/sklad-evidence/evidence` zůstává
 * vlastní sekcí (přesná shoda má přednost).
 */
export function klicSekceZCesty(pathname: string): string | undefined {
  let cesta = pathname.replace(/\/+$/, '') || '/';
  for (;;) {
    const klic = KLICE_PODLE_CESTY.get(cesta);
    if (klic) return klic;
    const konec = cesta.lastIndexOf('/');
    if (konec <= 0) return undefined;
    cesta = cesta.slice(0, konec);
  }
}

/** Fáze a krok sekce; neznámý klíč → undefined. */
export function fazeSekce(klic: string): FazeSekce | undefined {
  return FAZE_SEKCI[klic];
}
